import React, {Dispatch, SetStateAction, useEffect, useState} from "react";
import {Button, Col, Row, Select} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import {LogicEditorTypeId} from "../LogicEditor";
import {
    AssetAnnotationType, AssetRuleConditionType, AssetRuleType,
} from "../../../../../api/assetApi";
import {TAsset} from "../../../../../types";

const {Option} = Select;

type RulesConditionItemProps = {
    item: AssetRuleConditionType
    setRule: Dispatch<SetStateAction<AssetRuleType>>
    deleteCondition: (item: AssetRuleConditionType) => void
    annotations: AssetAnnotationType[]
    modelPart: TAsset[]
}

const RulesConditionItem = ({annotations, item, setRule, deleteCondition, modelPart}: RulesConditionItemProps) => {
    const logicalOperatorOptions = [
        {label: "=", value: LogicEditorTypeId["Equal"]},
        {label: "!=", value: LogicEditorTypeId["NotEqual"]},
        {label: ">=", value: LogicEditorTypeId["GreaterOrEqual"]},
        {label: "<=", value: LogicEditorTypeId["LessOrEqual"]},
        {label: ">", value: LogicEditorTypeId["GreaterThen"]},
        {label: "<", value: LogicEditorTypeId["LessThen"]},
    ];
    const eventsOptions = [
        {label: "On Click Annotation", value: LogicEditorTypeId["OnClickAnnotation"]},
        {label: "On Click Model Part", value: LogicEditorTypeId["OnClickModelPart"]},
        {label: "On Page Load", value: LogicEditorTypeId["OnPageLoad"]},
    ];
    const pageLoadOptions = [{value: "load", label: "load"}];
    const annotationsOptions = annotations.map(annotation => ({value: annotation.id, label: annotation.name}));
    const modelPartOptions = modelPart.map(part => ({value: part.dbId, label: part.patch?.name ? part.patch.name : `${part.type} ${part.id}`}));

    const [selectedValueOne, setSelectedValueOne] = useState<SelectedValueOneType>(null);
    const [selectedLogic, setSelectedLogic] = useState<SelectedLogicType>({
        value: LogicEditorTypeId["Equal"],
        disabled: true,
    });
    const [selectedValueTwo, setSelectedValueTwo] = useState<SelectedValueTwoType>({
        value: null,
        options: [],
        disabled: true,
    });

    const handleChangeRule = (cb: (condition: AssetRuleConditionType) => AssetRuleConditionType) => {
        setRule(prevState => {
            const state = prevState.condition.map(condition => {
                if (condition.appId) {
                    if (condition.appId === item.appId) {
                        return cb(condition);
                    }
                }

                if (condition.id) {
                    if (condition.id === item.id) {
                        return cb(condition);
                    }
                }

                return condition;
            });

            return {...prevState, condition: state};
        });
    }

    const handleChangeValueOne = (value: number) => {
        setSelectedValueOne(value);

        if (value === LogicEditorTypeId["OnPageLoad"]) {
            const state = {value: "load", options: pageLoadOptions, disabled: true};
            setSelectedValueTwo(state);

            handleChangeRule((condition) => (
                {
                    ...condition,
                    value: "load",
                    type_condition: value,
                    connection_id: 0,
                    connection_type: 0,
                }
            ));
        }

        if (value === LogicEditorTypeId["OnClickAnnotation"]) {
            const state = {value: null, options: annotationsOptions, disabled: false};
            setSelectedValueTwo(state);

            handleChangeRule((condition) => (
                {
                    ...condition,
                    value: "",
                    type_condition: value,
                    connection_id: 0,
                    connection_type: LogicEditorTypeId["Annotation"]
                }
            ));
        }

        if (value === LogicEditorTypeId["OnClickModelPart"]) {
            const state = {value: null, options: modelPartOptions, disabled: false};
            setSelectedValueTwo(state);

            handleChangeRule((condition) => (
                {
                    ...condition,
                    value: "",
                    type_condition: value,
                    connection_id: 0,
                    connection_type: LogicEditorTypeId["CustomizerId"]
                }
            ));
        }
    };

    const handleChangeValueTwo = (value: number | string) => {
        setSelectedValueTwo(prevState => ({...prevState, value}));

        if (selectedValueOne === LogicEditorTypeId["OnClickAnnotation"]) {
            handleChangeRule((condition) => (
                {...condition, connection_id: value as number}
            ));
        }

        if (selectedValueOne === LogicEditorTypeId["OnClickModelPart"]) {
            handleChangeRule((condition) => (
                {...condition, connection_id: value as number}
            ));
        }
    };

    useEffect(() => {
        const isAnnotationClickStatus = item.type_condition === LogicEditorTypeId["OnClickAnnotation"];
        const isModelPartClickStatus = item.type_condition === LogicEditorTypeId["OnClickModelPart"];
        const isPageLoadStatus = item.type_condition === LogicEditorTypeId["OnPageLoad"];

        if (isAnnotationClickStatus || isModelPartClickStatus || isPageLoadStatus) {
            setSelectedValueOne(item.type_condition);
        }

        if (isAnnotationClickStatus) {
            setSelectedValueTwo({value: item.connection_id, options: annotationsOptions, disabled: false});
        }

        if (isModelPartClickStatus) {
            setSelectedValueTwo({value: item.connection_id, options: modelPartOptions, disabled: false});
        }

        if (isPageLoadStatus) {
            setSelectedValueTwo({value: item.value, options: pageLoadOptions, disabled: true});
        }
    }, []);

    return (
        <Row style={{width: "100%", margin: 0}} gutter={[8, 0]}>
            <Col span={8}>
                <Select
                    showSearch
                    optionFilterProp="children"
                    value={selectedValueOne === 0 ? null : selectedValueOne}
                    onChange={handleChangeValueOne}
                >
                    {
                        eventsOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))
                    }
                </Select>
            </Col>

            <Col span={5}>
                <Select
                    value={selectedLogic.value}
                    disabled={selectedLogic.disabled}
                >
                    {
                        logicalOperatorOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))
                    }
                </Select>
            </Col>

            <Col span={8}>
                <Select
                    value={selectedValueTwo.value === 0 ? null : selectedValueTwo.value}
                    disabled={selectedValueTwo.disabled}
                    onChange={handleChangeValueTwo}
                    options={selectedValueTwo.options}
                />
            </Col>

            <Col span={3}>
                <Button
                    onClick={() => deleteCondition(item)}
                    style={{minWidth: 32, maxWidth: 32}}
                    icon={<DeleteOutlined/>}
                />
            </Col>
        </Row>
    );
};

export default RulesConditionItem;

type SelectedLogicType = {
    value: number | null
    disabled: boolean
}

type SelectedValueOneType = number | null

type SelectedValueTwoType = {
    value: number | string | null
    options: { value: string | number, label: string }[]
    disabled: boolean
}
